import update from 'immutability-helper';
import get from 'lodash/get';
import { orderBy } from 'natural-orderby';
import * as ACTIONS from '../actions';
import { COLUMNS, generateColumns } from '../constants';
import moment from 'moment';

const parseAdditionalData = (lead) => {
  if (lead.additional_data && typeof lead.additional_data === 'string') {
    try {
      return {
        ...lead,
        additional_data: JSON.parse(lead.additional_data)
      };
    } catch (e) {
      return lead;
    }
  }
  return lead;
};

const orderASC = (leads) => {
  const ordered = orderBy(leads, [(l) => {
    const task = get(l, 'additional_data.nextPendingTask');
    return task && task.due_at ? moment(task.due_at).valueOf() : null; 
  }], ['asc']);
  return ordered;
};

// Helper function to find column by id (handles both numeric and string ids)
const findColumnById = (columns, id) => {
  return columns.find(col => col.id === id || col.id === parseInt(id));
};

// Helper function to map lead to appropriate column based on cadence progress
const mapLeadToColumn = (lead, columns) => {
  // First, check if the lead has an explicit column_id set to Positivo or Negativo
  if (lead.column_id === 4) return columns.find(col => col.id === 4);
  if (lead.column_id === 5) return columns.find(col => col.id === 5);

  // For Positivo and Negativo columns, only show leads that have their column_id explicitly set
  // This prevents leads from being automatically placed in these columns based on cadence status
  const positivoColumn = columns.find(col => col.id === 4);
  const negativoColumn = columns.find(col => col.id === 5);
  
  // If no cadence data at all, put in Inicio column
  if (!lead.additional_data || !lead.additional_data.cadences || lead.additional_data.cadences.length === 0) {
    return columns.find(col => col.id === 1);
  }
  
  // Check cadence progress to determine which step column
  const cadences = lead.additional_data.cadences;
  for (const cadence of cadences) {
    if (cadence.data && Array.isArray(cadence.data)) {
      // Check if any tasks are done
      const hasCompletedTasks = cadence.data.some(task => task.status === 'done');
      
      if (!hasCompletedTasks) {
        // If no tasks are done yet, put in Leads column
        return columns.find(col => col.id === 2);
      }
      
      // Find the latest completed task to determine the current step column
      const completedTasks = cadence.data.filter(task => task.status === 'done');
      if (completedTasks.length > 0) {
        // Get the latest completed task (last in the array)
        const latestCompletedTask = completedTasks[completedTasks.length - 1];
        const stepColumn = columns.find(col => col.id === `step_${latestCompletedTask.uid}`);
        if (stepColumn) {
          return stepColumn;
        }
        // If step column not found, fall back to Leads column
        return columns.find(col => col.id === 2);
      }
      
      // Note: Canceled tasks no longer automatically move leads to Negativo column
      // Leads must be explicitly moved to Negativo column via the drawer
    }
  }

  // If has cadence data but no steps are done, put in Leads column
  return columns.find(col => col.id === 2);
};

const columnsReducer = (state = COLUMNS, action) => {
  // Ensure we always have a valid state
  const currentState = state || COLUMNS;
  
  switch (action.type) {
    case ACTIONS.LOAD.SUCCESS: {
      const handledState = action.payload.data.data.map(parseAdditionalData);
      
      // If we have dynamic columns, map leads to appropriate columns
      if (currentState.some(col => col.type === 'step')) {
        return currentState.map((column) => {
          // Get leads from server that should be in this column
          const serverLeadsForColumn = handledState.filter(lead => {
            const targetColumn = mapLeadToColumn(lead, currentState);
            return targetColumn && targetColumn.id === column.id;
          });
          
          // Get existing leads in this column that are not in the server response
          // (these might be recently updated leads that haven't been synced yet)
          const existingLeadsNotInServer = column.data.filter(existingLead => 
            !handledState.some(serverLead => serverLead.id === existingLead.id)
          );
          
          // Get recently updated leads (within last 30 seconds) that should be preserved
          const recentlyUpdatedLeads = column.data.filter(existingLead => {
            if (!existingLead._lastUpdated) return false;
            const timeSinceUpdate = Date.now() - existingLead._lastUpdated;
            const isRecentlyUpdated = timeSinceUpdate < 30000; // 30 seconds
            return isRecentlyUpdated;
          });
          
          // For recently updated leads, use the existing version instead of server version
          const serverLeadsWithoutRecentlyUpdated = serverLeadsForColumn.filter(serverLead => 
            !recentlyUpdatedLeads.some(recentLead => recentLead.id === serverLead.id)
          );
          
          // Combine server leads with recently updated leads
          const combinedLeads = [...serverLeadsWithoutRecentlyUpdated, ...recentlyUpdatedLeads, ...existingLeadsNotInServer];
          
          return {
            ...column,
            data: column.id === 1 ? orderASC(combinedLeads) : combinedLeads,
            total: combinedLeads.length,
          };
        });
      }
      
      // Legacy behavior for backward compatibility
      return currentState.map((column) => {
        const currentColumn = handledState.filter((c) => c.column_id === column.id);
        return {
          ...column,
          data: column.id === 2 ? orderASC(currentColumn) : currentColumn,
          total: currentColumn.length,
        };
      });
    }

    case ACTIONS.CREATE.SUCCESS: {
      const createdLead = parseAdditionalData(get(action, 'payload.data.data'));
      
      // If we have dynamic columns, map the new lead to appropriate column
      if (currentState.some(col => col.type === 'step')) {
        const targetColumn = mapLeadToColumn(createdLead, currentState);
        if (targetColumn) {
          return currentState.map((column) => {
            if (column.id !== targetColumn.id) return column;
            return {
              ...column,
              data: column.id === 1
                ? orderASC([createdLead, ...column.data])
                : [createdLead, ...column.data],
              total: column.data.length + 1
            };
          });
        }
      }
      
      // Legacy behavior
      return currentState.map((column) => {
        if (column.id !== createdLead.column_id) return column;
        return {
          ...column,
          data: column.id === 2
            ? orderASC([createdLead, ...column.data])
            : [createdLead, ...column.data],
          total: column.data.length + 1
        };
      });
    }

    case ACTIONS.BATCH.SUCCESS: {
      const batchData = get(action, 'payload.data.data');
      const createdLeads = batchData && batchData.created ? batchData.created : (Array.isArray(batchData) ? batchData : []);
      const leads = createdLeads.map(parseAdditionalData);
      
      // If we have dynamic columns, map leads to appropriate columns
      if (currentState.some(col => col.type === 'step')) {
        return currentState.map((column) => {
          const columnLeads = leads.filter(lead => {
            const targetColumn = mapLeadToColumn(lead, currentState);
            return targetColumn && targetColumn.id === column.id;
          });
          
          return {
            ...column,
            data: column.id === 1
              ? orderASC([...column.data, ...columnLeads])
              : [...column.data, ...columnLeads]
          };
        });
      }
      
      // Legacy behavior
      return currentState.map((column) => {
        const columnLeads = leads.filter(l => l.column_id === column.id);
        return {
          ...column,
          data: column.id === 2
            ? orderASC([...column.data, ...columnLeads])
            : [...column.data, ...columnLeads]
        };
      });
    }

    case ACTIONS.SAVE.INIT: {
      const lead = get(action, 'payload.request.data');
      return currentState.map((column) => {
        if (column.id !== lead.column_id) return column;
        return {
          ...column,
          data: column.data.map(l => l.id === lead.id ? {...l, ...lead, isLoading: true} : l)
        };
      });
    }

    case ACTIONS.SAVE.SUCCESS: {
      const updatedLead = parseAdditionalData(get(action, 'payload.data.data'));
      
      // Add a timestamp to track recently updated leads
      const leadWithTimestamp = {
        ...updatedLead,
        _lastUpdated: Date.now()
      };
      
      // If we have dynamic columns, handle the update
      if (currentState.some(col => col.type === 'step')) {
        const targetColumn = mapLeadToColumn(leadWithTimestamp, currentState);
        
        return currentState.map((column) => {
          const hasLead = column.data.some(l => l.id === updatedLead.id);
          const isTargetColumn = targetColumn && targetColumn.id === column.id;
          
          if (hasLead && isTargetColumn) {
            // Update the lead in the target column
            return {
              ...column,
              data: column.id === 1 
                ? orderASC(column.data.map(l => l.id === updatedLead.id ? leadWithTimestamp : l))
                : column.data.map(l => l.id === updatedLead.id ? leadWithTimestamp : l),
            };
          } else if (hasLead && !isTargetColumn) {
            // Remove the lead from this column (it will be added to target column)
            return {
              ...column,
              data: column.data.filter(l => l.id !== updatedLead.id),
            };
          } else if (!hasLead && isTargetColumn) {
            // Add the lead to the target column
            return {
              ...column,
              data: column.id === 1 
                ? orderASC([leadWithTimestamp, ...column.data])
                : [leadWithTimestamp, ...column.data],
            };
          } else {
            // Lead not in this column and this is not the target column
            return column;
          }
        });
      }
      
      // Legacy behavior
      return currentState.map((column) => {
        if (column.id === updatedLead.column_id) {
          return {
            ...column,
            data: column.data.map(l => l.id === updatedLead.id ? leadWithTimestamp : l),
          };
        } else if (column.data.some(l => l.id === updatedLead.id)) {
          return {
            ...column,
            data: column.data.filter(l => l.id !== updatedLead.id),
          };
        }
        return column;
      });
    }

    case ACTIONS.DELETE.SUCCESS: {
      const lead = get(action, 'meta.previousAction.payload.lead');
      return currentState.map((column) => {
        if (column.data.some(l => l.id === lead.id)) {
          return {
            ...column,
            data: column.data.filter((l) => l.id !== lead.id),
          };
        }
        return column;
      });
    }

    case ACTIONS.BULK_DELETE.SUCCESS: {
      const leadIds = get(action, 'meta.previousAction.payload.leadIds', []);
      return currentState.map((column) => ({
        ...column,
        data: column.data.filter(l => !leadIds.includes(l.id))
      }));
    }

    case ACTIONS.MOVE.INIT: {
      const sourceClone = Array.from(action.props[0]);
      const destClone = Array.from(action.props[1]);
      const droppableSource = action.props[2];
      const droppableDestination = action.props[3];
      const [removed] = sourceClone.splice(droppableSource.index, 1);
      destClone.splice(droppableDestination.index, 0, removed);

      return currentState.map((column) => {
        const sourceId = droppableSource.droppableId;
        const destId = droppableDestination.droppableId;
        
        // Handle both numeric and string IDs
        const columnId = typeof column.id === 'string' ? column.id : column.id.toString();
        
        if (columnId !== sourceId && columnId !== destId) {
          return column;
        }
        
        if (columnId === sourceId) {
          return {
            ...column,
            data: sourceClone
          };
        }
        
        if (columnId === destId) {
          return {
            ...column,
            data: destClone
          };
        }
        
        return column;
      });
    }

    case ACTIONS.MOVE.SUCCESS: {
      const lead = parseAdditionalData(get(action, 'payload.data.data'));
      return currentState.map((column) => {
        if (column.data.some(l => l.id === lead.id)) {
          return {
            ...column,
            data: column.id === 5 
              ? orderASC(column.data.map(l => l.id === lead.id ? lead : l))
              : column.data.map(l => l.id === lead.id ? lead : l)
          };
        }
        return column;
      });
    }

    // New action to update columns when cadence changes
    case 'UPDATE_COLUMNS_FOR_CADENCE': {
      const { cadenceData } = action.payload;
      
      // Generate new column structure
      const newColumns = generateColumns(cadenceData);
      
      // Preserve existing lead data by mapping it to the new column structure
      const existingLeads = currentState.reduce((acc, column) => {
        if (column.data && column.data.length > 0) {
          acc.push(...column.data);
        }
        return acc;
      }, []);
      
      // Map existing leads to new columns
      const updatedColumns = newColumns.map((newColumn) => {
        const columnLeads = existingLeads.filter(lead => {
          const targetColumn = mapLeadToColumn(lead, newColumns);
          return targetColumn && targetColumn.id === newColumn.id;
        });
        
        return {
          ...newColumn,
          data: newColumn.id === 1 ? orderASC(columnLeads) : columnLeads,
          total: columnLeads.length,
        };
      });
      
      return updatedColumns;
    }

    default:
      return currentState;
  }
};

export default columnsReducer; 